import { ButtonType, ButtonWidget, CheckBoxWithLabelWidget, DropdownButtonType, List, ListItem, SingleSelectDropdown, StackingRootContext, TextLinkWidget } from '@tableau/widgets';
import React from 'react';
import _ from 'underscore';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { openReactAlertDialog } from '~src/commonInfra/dialogs/ReactAlertDialog';
import { UserSettingsStyle } from '~src/userSettings/styles/UserSettingsStyle';

import { Messages } from '../../../codegen/Localize';
import { openReactConfirmationDialog } from '../../../commonInfra/dialogs/ReactConfirmationDialog';
import { TabDialogWidth } from '../../../commonInfra/dialogs/style/TabDialogWidth';
import { IApiToken } from '../../../httpClient/serverApi/types/IApiToken';
import {getSessionManagementServiceInstance} from '../../../sessionManagement/SessionManagementService';
import { ISiteScimConfiguration } from '../serverApi/types/ISiteScimConfiguration';
import { IAuthenticationProvider, SiteAuthenticationSettingsService } from '../services/SiteAuthenticationSettingsService';
import { authDropdownStyle } from '../styles/SiteAuthenticationSettingsStyles';


export interface ScimSettingsProps {
    samlProviders: IAuthenticationProvider[];
    scimProviderDropDownListItems: ListItem[];
    setEnabledSAMLConfigForSCIM: (setEnabledSAMLConfigForSCIM: string) => void;
    selectedSAMLProviderId: string;
}

export interface ScimSettingsState {
    scimProvisioningEnabled: boolean;
    baseUrl: string;
    secret: string;
    selectedSAMLProviderId: string;
    currentSAMLProviderId: string;
}

export class ScimSettings extends React.Component<ScimSettingsProps, ScimSettingsState> {

    private SCIM_CLIENT_API_TOKEN = 'SCIM_CLIENT_API_TOKEN';
    private SCIM_CLIENT_API_TOKEN_PREFIX = this.SCIM_CLIENT_API_TOKEN + ':';

    constructor(props: ScimSettingsProps) {
        super(props);
        this.state = {
            scimProvisioningEnabled: false,
            baseUrl: '',
            secret: null,
            selectedSAMLProviderId: undefined,
            currentSAMLProviderId: undefined,
        };
    }

    public componentDidMount() {
        if (!FeatureFlags.featureFlagForMultipleIDPSupportEnabled) {
            this.initScimApiToken();
            return;
        }
        this.initSAMLDropdown();
    }

    private selectAll(event: any): void {
        event.target.select();
    }

    private openSCIMHelp(): void {
        window.open('https://onlinehelp.tableau.com/current/online/en-us/scim_config_online.htm', '_blank');
    }

    private getServerApiTokens = () => {
        return getSessionManagementServiceInstance().getApiTokens();
    };

    private getServerSessionSiteLuid = () => {
        return getSessionManagementServiceInstance().sessionSiteLuid();
    };

    private serverDeleteApiToken = (tokenId: string) => {
        return getSessionManagementServiceInstance().deleteApiToken(tokenId);
    };

    private serverCreateApiToken = (clientName: string) => {
        return getSessionManagementServiceInstance().createApiToken(clientName);
    };

    private getSiteScimConfiguration = () => {
        return SiteAuthenticationSettingsService.getSiteScimConfiguration();
    };

    private createOrUpdateScimConfiguration = (config: ISiteScimConfiguration) => {
        return SiteAuthenticationSettingsService.createOrUpdateScimConfiguration(config);
    };

    private initScimApiToken() {
        this.getServerApiTokens().then((allApiTokens: IApiToken[]) => {
            const scimApiTokens: IApiToken[] = allApiTokens.
            filter(token => token.clientName === this.SCIM_CLIENT_API_TOKEN);

            this.setScimProvisioning(scimApiTokens.length > 0);
            this.setBaseUrl(this.getBaseUrl(this.state.scimProvisioningEnabled));
        });
    }

    private initSAMLDropdown() {
        this.getSiteScimConfiguration().then((config: ISiteScimConfiguration) => {
            const scimIdpConfigurationIds = config.idpConfigurationIds;
            const isScimEnabled = scimIdpConfigurationIds && scimIdpConfigurationIds.length > 0;
            this.setScimProvisioning(isScimEnabled);

            if (isScimEnabled) {
                const currentSAMLProviderId = scimIdpConfigurationIds[0];
                this.setState({selectedSAMLProviderId: currentSAMLProviderId, currentSAMLProviderId});
            }

            this.setBaseUrl(this.getBaseUrl(isScimEnabled));
        });
    }

    private getBaseUrl(scimEnabled: boolean): string {
        if (!scimEnabled) {
            return 'https://';
          }
        const podName = window.location.hostname === 'localhost' ?
            'local-pod-fake' : window.location.hostname.split('.')[0];
        return 'https://scim.online.tableau.com/pods/' + podName +
            '/sites/' + this.getServerSessionSiteLuid() + '/scim/v2';
    }

    private SCIMCannotBeEnabledDialog(): Promise<any> {
        const dialogInstance = openReactAlertDialog({
          title: Messages.siteAuthenticationCannotEnableScim(),
          message: Messages.siteAuthenticationCannotEnableScimMessage(),
          dialogWidth: TabDialogWidth.width375px,
        });

        return Promise.resolve(dialogInstance?.result);
    }

    toggleScimProvisioning = (checked: boolean) => {
        if (FeatureFlags.featureFlagForMultipleIDPSupportEnabled && this.props.scimProviderDropDownListItems?.length === 0) {
            return this.SCIMCannotBeEnabledDialog();
        }
        var result;
        if (this.state.scimProvisioningEnabled) {
            result = this.deleteScimApiTokens().then(() => {
                if (FeatureFlags.featureFlagForMultipleIDPSupportEnabled) {
                    this.props.setEnabledSAMLConfigForSCIM('');
                    this.setState({currentSAMLProviderId: null, selectedSAMLProviderId: null});
                    return this.setSiteScimConfiguration([]);
                }
                return Promise.resolve();
            });
        } else {
            if (FeatureFlags.featureFlagForMultipleIDPSupportEnabled) {
                result = Promise.resolve();
            } else {
                result = this.createScimApiToken();
            }
        }
        result.then(() => {
            this.setScimProvisioning(checked);
            this.setBaseUrl(this.getBaseUrl(this.state.scimProvisioningEnabled));
        }).catch(err => err);
    };

    alertAndDeleteOldTokensAndGenerateNewToken<TResult>(): void {
        openReactConfirmationDialog<void, TResult>({
            title: Messages.generateScimSecret(),
            message: Messages.siteAuthenticationScimSecretGenerateConfirmBody(),
            paramsForAction: undefined,
            action: () => {
                const idpConfigurationId = FeatureFlags.featureFlagForMultipleIDPSupportEnabled ? this.state.currentSAMLProviderId : null;
                return this.deleteScimApiTokens().then(() => this.createScimApiToken(idpConfigurationId));
            },
            actionName: Messages.generateScimSecret(),
            isActionDelete: false,
            dialogWidth: TabDialogWidth.width375px
        });
    }

    deleteScimApiTokens(): Promise<any> {
        return this.notifyOnFail(this.getServerApiTokens().
        then((allApiTokens: IApiToken[]) => {
            const scimApiTokens: IApiToken[] = allApiTokens.
            filter(token => token.clientName.startsWith(this.SCIM_CLIENT_API_TOKEN));

            scimApiTokens.map(token => this.serverDeleteApiToken(token.id));
        }).then(() => this.setSecret(null)));
    }

    createScimApiToken(idpConfigurationId?: string): Promise<any> {
        const clientName = idpConfigurationId ? this.SCIM_CLIENT_API_TOKEN_PREFIX + idpConfigurationId : this.SCIM_CLIENT_API_TOKEN;
        return this.notifyOnFail(this.serverCreateApiToken(clientName).
        then((newApiToken: IApiToken) => this.setSecret(newApiToken.secret)));
    }

    setSiteScimConfiguration(idpConfigurationIds: string[]): Promise<any> {
        return this.notifyOnFail(this.createOrUpdateScimConfiguration({ idpConfigurationIds }));
    }

    private selectSAMLConfigurationforSCIM(id: string) {
        const selectedSAMLProvider = _.find(this.props.samlProviders, (provider: IAuthenticationProvider) => (provider.id === id));
        this.setState({ selectedSAMLProviderId:  selectedSAMLProvider?.id});
    }

    private cancelSCIMOnClick() {
        this.setState({ selectedSAMLProviderId: this.state.currentSAMLProviderId });
    }

    private saveSCIMOnClick() {
        this.createScimApiToken(this.state.selectedSAMLProviderId)
        .then(() => {
            this.setSiteScimConfiguration([ this.state.selectedSAMLProviderId ]);
        })
        .then(() => {
            // TODO: Show success or error message
            this.setState({ currentSAMLProviderId: this.state.selectedSAMLProviderId });
            this.props.setEnabledSAMLConfigForSCIM(this.state.selectedSAMLProviderId);
        });
        return;
    }

    private setSecret(tokenSecret: string): void {
        return this.setState({secret: tokenSecret});
    }

    private setScimProvisioning(enabled: boolean): void {
        return this.setState({scimProvisioningEnabled: enabled});
    }

    private setBaseUrl(url: string): void {
        return this.setState({baseUrl: url});
    }

    private notifyOnFail<TResult>(result: Promise<TResult>): Promise<TResult> {
        result.catch(() => {
            ToasterService.instance.pop({ type: ToastType.Error, text: Messages.unexpectedError()});
        });
        return result;
    }

    private dropdownListContainsId(id: string): boolean {
        const selectedSAMLProvider = _.find(this.props.samlProviders, (provider: IAuthenticationProvider) => (provider.id === id));
        return !!selectedSAMLProvider;
    }

    private renderScimSettings(): JSX.Element {
        return (
            <>
            <div className="tb-settings-group"  style={{ paddingLeft: 20}}>
                <div className="tb-settings-group-name">
                    {Messages.scimSettingsHeading()}
                </div>
                <div className="tb-indent">
                    <div className="tb-settings-control">
                    {Messages.siteAuthenticationSelectSCIM()}
                    &nbsp;
                    <TextLinkWidget onClick={this.openSCIMHelp} testId={'scim-learn-more-link'}>
                        {Messages.scimLearnMore()}
                    </TextLinkWidget>
                    </div>
                    <CheckBoxWithLabelWidget
                    label={Messages.scimSettingsEnable()}
                    checked={this.state.scimProvisioningEnabled}
                    testId={'scim-checkbox'}
                    disabled={false}
                    removeLeftMargin={true}
                    handleChange={this.toggleScimProvisioning}/>
                    <div className="tb-auth-options-row">
                        <div className="tb-auth-options-label">
                            {Messages.siteAuthenticationScimBaseUrl()}
                        </div>
                        <input className="tb-readonly-field" type="text" value={this.state.baseUrl} disabled={!this.state.scimProvisioningEnabled} readOnly></input>
                    </div>
                    <div className="tb-auth-options-list">
                        <table>
                            <tbody>
                            <tr>
                                <td>
                                    <ButtonWidget
                                    buttonType={ButtonType.Outline}
                                    onClick={this.alertAndDeleteOldTokensAndGenerateNewToken.bind(this)}
                                    testId={'scim-create-apitoken'}
                                    disabled={!this.state.scimProvisioningEnabled || (FeatureFlags.featureFlagForMultipleIDPSupportEnabled && (this.state.selectedSAMLProviderId !== this.state.currentSAMLProviderId))}>
                                        {Messages.generateScimSecret()}
                                    </ButtonWidget>
                                </td>
                                {this.state.secret ?
                                <td className="tb-auth-options-list-item">
                                <input className="tb-readonly-field" type="text" value={this.state.secret} onClick={this.selectAll} readOnly></input>
                                &nbsp;
                                 </td>
                                : null}
                            </tr>
                            <tr>
                                <td>&nbsp;</td>
                                {this.state.secret ?
                                <td className="tb-auth-options-list-item">
                                <div className="tb-settings-msg">
                                    {Messages.scimSecretInfo()}
                                </div>
                                </td>
                                : null}
                            </tr>
                            </tbody>
                        </table>
                    </div>
                   {FeatureFlags.featureFlagForMultipleIDPSupportEnabled ?
                        <div>
                            <div>
                                <StackingRootContext.Provider value={null}>
                                    <SingleSelectDropdown
                                        list={<List items={this.props.scimProviderDropDownListItems} style={{maxWidth: 330}}/>}
                                        onChange={this.selectSAMLConfigurationforSCIM.bind(this)}
                                        value={(this.state.selectedSAMLProviderId && this.dropdownListContainsId(this.state.selectedSAMLProviderId))? this.state.selectedSAMLProviderId : Messages.none() }
                                        label={Messages.authentication()}
                                        type={DropdownButtonType.Outline}
                                        style={authDropdownStyle}
                                        disabled={this.props.samlProviders?.length === 0 || !this.state.scimProvisioningEnabled}
                                        testId='authentication-for-scim-dropdown'
                                    />
                                </StackingRootContext.Provider>
                            </div>
                            <div className="tb-settings-control">
                                <span className={UserSettingsStyle.cancelButton}>
                                    <ButtonWidget
                                        type="reset"
                                        buttonType={ButtonType.Outline}
                                        onClick={this.cancelSCIMOnClick.bind(this)}
                                        disabled={!this.state.scimProvisioningEnabled}
                                    >
                                        {Messages.clear()}
                                    </ButtonWidget>
                                </span>
                                <span>
                                    <ButtonWidget
                                        type="submit"
                                        buttonType={ButtonType.Outline}
                                        onClick={this.saveSCIMOnClick.bind(this)}
                                        disabled={!this.state.scimProvisioningEnabled || !this.state.selectedSAMLProviderId || this.state.selectedSAMLProviderId === this.state.currentSAMLProviderId}
                                        testId={'save-changes-button'}
                                    >
                                        {Messages.save()}
                                    </ButtonWidget>
                                </span>
                            </div>
                        </div>
                    : null}
                </div>
            </div>
            </>
        );
    }

    public render(): JSX.Element {
        return FeatureFlags.scimApiTokenEnabled ?
            this.renderScimSettings()
        : null;
    }

}
